import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetch } from "./api";
import { pushSupport, currentSubscription, enablePush, disablePush } from "./pushClient";

// New-review alerts (G7, GOOGLE_SYNC_PLAN.md). Each person's own settings:
// email (account-wide) and push (per device, this one).
const NotificationSettings: React.FC = () => {
  const { t } = useTranslation();
  const [emailNewReview, setEmailNewReview] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [push, setPush] = useState<{ configured: boolean; publicKey: string | null } | null>(null);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushNote, setPushNote] = useState("");
  const support = pushSupport();
  const denied = support === "supported" && Notification.permission === "denied";

  useEffect(() => {
    apiFetch("/api/push/config")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => setPush(data ? { configured: !!data.configured, publicKey: data.publicKey } : null))
      .catch(() => setPush(null));
    if (support === "supported" && Notification.permission === "granted") {
      currentSubscription().then((sub) => setPushOn(!!sub)).catch(() => setPushOn(false));
    }
  }, [support]);

  const togglePush = async () => {
    if (!push?.publicKey) return;
    setPushBusy(true);
    setPushNote("");
    if (pushOn) {
      await disablePush();
      setPushOn(false);
    } else {
      const result = await enablePush(push.publicKey);
      setPushOn(result === "on");
      if (result === "denied") setPushNote(t("notifications.pushDenied"));
      if (result === "failed") setPushNote(t("notifications.pushFailed"));
    }
    setPushBusy(false);
  };

  const sendTest = async () => {
    setPushBusy(true);
    setPushNote("");
    try {
      const res = await apiFetch("/api/push/test", { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (res.status === 429) setPushNote(t("notifications.testWait"));
      else if (!res.ok || !data.sent) setPushNote(t("notifications.testFailed"));
      else setPushNote(t("notifications.testSent"));
    } catch {
      setPushNote(t("notifications.testFailed"));
    } finally {
      setPushBusy(false);
    }
  };

  useEffect(() => {
    apiFetch("/api/me/notifications")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => setEmailNewReview(data ? data.emailNewReview !== false : true))
      .catch(() => setEmailNewReview(true));
  }, []);

  const toggleEmail = async () => {
    if (emailNewReview === null) return;
    const next = !emailNewReview;
    setSaving(true);
    setError("");
    try {
      const res = await apiFetch("/api/me/notifications", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emailNewReview: next }),
      });
      if (!res.ok) throw new Error();
      setEmailNewReview(next);
    } catch {
      setError(t("notifications.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-6 bg-white rounded-xl shadow p-4 space-y-3">
      <div className="font-medium">{t("notifications.title")}</div>
      <label className="flex items-center justify-between gap-4 text-sm">
        <span>
          <span className="block font-medium">{t("notifications.emailNewReview")}</span>
          <span className="block text-gray-500">{t("notifications.emailNewReviewHelp")}</span>
        </span>
        <input
          type="checkbox"
          className="h-5 w-5 accent-teal-600"
          checked={emailNewReview === true}
          disabled={emailNewReview === null || saving}
          onChange={toggleEmail}
        />
      </label>
      {error && <div className="text-sm text-red-600">{error}</div>}

      {push?.configured && (
        <div className="border-t pt-3 space-y-2 text-sm">
          {support === "supported" && (
            <>
              <label className="flex items-center justify-between gap-4">
                <span>
                  <span className="block font-medium">{t("notifications.pushThisDevice")}</span>
                  <span className="block text-gray-500">{t("notifications.pushThisDeviceHelp")}</span>
                </span>
                <input
                  type="checkbox"
                  className="h-5 w-5 accent-teal-600"
                  checked={pushOn}
                  disabled={pushBusy || (denied && !pushOn)}
                  onChange={togglePush}
                />
              </label>
              {denied && !pushOn && <div className="text-amber-700">{t("notifications.pushDenied")}</div>}
              {pushOn && (
                <button onClick={sendTest} disabled={pushBusy} className="text-teal-600 underline disabled:opacity-50">
                  {t("notifications.sendTest")}
                </button>
              )}
            </>
          )}
          {support === "ios-needs-install" && (
            <div>
              <div className="font-medium">{t("notifications.pushThisDevice")}</div>
              <div className="text-gray-600">{t("notifications.iosInstall")}</div>
            </div>
          )}
          {support === "unsupported" && <div className="text-gray-500">{t("notifications.pushUnsupported")}</div>}
          {pushNote && <div className="text-gray-700">{pushNote}</div>}
        </div>
      )}
    </div>
  );
};

export default NotificationSettings;
